import type { UploadedFile } from '@engagement-web-app/common';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, type ErrorComponentProps, useRouter } from '@tanstack/react-router';
import { useCallback } from 'react';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { FileListTable, FileListTableSkeleton } from '@/components/FileListTable';
import { Flex } from '@/components/Flex';
import { Pill } from '@/components/Pill';
import { Section, SectionHeader } from '@/components/Section';
import { Skeleton } from '@/components/Skeleton';
import { showErrorToast, showSuccessToast } from '@/components/Toasts';
import { deleteFile, fetchFileDownloadUrl, fetchOrganizationFiles } from '@/queries/files';
import { fetchOrganization } from '@/queries/organizations';
import { FileUploadButton } from '../-fragments/FileUploadButton';

export const Route = createFileRoute('/system/_auth/organizations/$organizationId/reports/')({
    loader: async ({ params }) => {
        const { organizationId } = params;
        const [org, files] = await Promise.all([
            fetchOrganization({ uuid: organizationId }),
            fetchOrganizationFiles(organizationId, { from: 0 }),
        ]);
        return { org, files };
    },
    pendingComponent: SystemShowOrganizationReportsRouteSkeleton,
    errorComponent: SystemShowOrganizationReportsRouteError,
    component: SystemShowOrganizationReportsRoute,
});

function SystemShowOrganizationReportsRouteError({ reset }: ErrorComponentProps) {
    return <ErrorOverlay active onRetry={reset} />;
}

function SystemShowOrganizationReportsRoute() {
    const { org, files } = Route.useLoaderData();
    const router = useRouter();

    const handleDownload = useCallback(
        async (file: UploadedFile) => {
            try {
                const { downloadUrl } = await fetchFileDownloadUrl(org.uuid, file.fileId);
                window.open(downloadUrl, '_blank');
            } catch (error) {
                console.error('Failed to download file:', error);
                showErrorToast({
                    title: 'ファイルのダウンロード',
                    description: 'ファイルのダウンロードに失敗しました。',
                });
            }
        },
        [org.uuid]
    );

    const deleteFileMutation = useMutation({
        mutationFn: (file: UploadedFile) => deleteFile(org.uuid, file.fileId),
        onSuccess() {
            router.invalidate();
            showSuccessToast({ title: 'ファイル削除', description: 'ファイルが削除されました。' });
        },
        onError(error) {
            console.error('Failed to delete file:', error);
            showErrorToast({ title: 'ファイル削除', description: 'ファイルの削除に失敗しました。' });
        },
    });

    return (
        <Section>
            <SectionHeader
                title="レポート一覧"
                extras={
                    <Flex justify="space-between" align="center" gap="2">
                        <Pill>{files.total}件</Pill>
                        <FileUploadButton organizationId={org.uuid} organizationName={org.name} />
                    </Flex>
                }
            />

            <FileListTable
                files={files.list}
                onDownload={handleDownload}
                onDelete={deleteFileMutation.mutateAsync}
                isDeleting={deleteFileMutation.isPending}
            />
        </Section>
    );
}

function SystemShowOrganizationReportsRouteSkeleton() {
    return (
        <Section>
            <SectionHeader
                title="レポート一覧"
                extras={
                    <Flex justify="space-between" align="center" gap="2">
                        <Skeleton.Line width={50} height="xl" />
                        <Skeleton.Box width={140} height={36} />
                    </Flex>
                }
            />

            <FileListTableSkeleton />
        </Section>
    );
}
